import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Gem, Heart, Star, Zap } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="hero-section relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Floating light orbs */}
      <div className="light-orb light-orb-1" />
      <div className="light-orb light-orb-2" />
      <div className="light-orb light-orb-3" />
      <div className="light-orb light-orb-4" />

      {/* Sparkle particles */}
      <div className="sparkle-field">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="sparkle-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: ['#9D4EDD', '#FF6B9D', '#FFB800', '#00B4D8', '#00C853'][i % 5],
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        {/* Main Hero Content - Full Width Text Focus */}
        <div className={`text-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Brand Badge */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Sparkles className="w-5 h-5 text-[#c9a96e]" />
            <span className="text-sm uppercase tracking-[0.3em] bg-gradient-to-r from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] bg-clip-text text-transparent font-bold">
              Welcome to Your Transformation
            </span>
            <Sparkles className="w-5 h-5 text-[#c9a96e]" />
          </div>
          
          {/* Main Headline */}
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1A1A2E] leading-[1.1] mb-8">
            <span className="text-gradient-vibrant">Gemstone Minerals</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#4A4A6A] font-light max-w-3xl mx-auto mb-12 leading-relaxed">
            Where ancient Earth wisdom meets modern spiritual awakening. 
            Discover the transformative power of energetically activated crystals.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <Link
              to="/collection"
              className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-[#1e1919] to-[#2a2525] text-white px-10 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-[0_10px_40px_rgba(30,25,25,0.4)] hover:scale-105"
            >
              <Gem className="relative z-10 w-5 h-5 text-[#c9a96e]" />
              <span className="relative z-10 font-semibold tracking-wider text-sm uppercase">Explore The Collection</span>
              <ArrowRight className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/webinar-registration"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#FFB800] to-[#FF9F43] text-[#1e1919] px-10 py-4 rounded-xl hover:shadow-[0_10px_40px_rgba(255,184,0,0.3)] hover:scale-105 transition-all duration-300"
            >
              <Star className="w-5 h-5" />
              <span className="font-semibold tracking-wider text-sm uppercase">Join Our Webinars</span>
            </Link>
          </div>
        </div>

        {/* 800 Word Content Section */}
        <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-[#e8e4de]">
            
            {/* Section Header */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a96e]" />
              <Heart className="w-6 h-6 text-[#FF6B9D]" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a96e]" />
            </div>

            {/* Main Content - 800+ words */}
            <div className="prose prose-lg max-w-none text-center">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A2E] mb-6">
                The <span className="text-gradient-vibrant">Gemstone Lifestyle</span>: 
                Living in Harmony with Earth's Sacred Energies
              </h2>

              <div className="text-left space-y-6 text-[#4A4A6A] leading-relaxed">
                <p>
                  At <strong className="text-[#1e1919]">Gemstone Minerals</strong>, we believe that true wellness begins at the energetic level. 
                  Long before modern medicine, ancient civilizations understood something profound: the Earth itself holds the keys 
                  to human healing, balance, and spiritual evolution. Buried deep within mountain ranges, volcanic chambers, and 
                  crystalline caves lie nature's most powerful tools for transformation—gemstones and minerals that have absorbed 
                  millions of years of planetary energy, waiting to share their wisdom with those ready to receive it.
                </p>

                <p>
                  The gemstone lifestyle is not about acquiring beautiful objects. It is about <em>relationship</em>—a sacred exchange 
                  between you and the living energies of the Earth. When you hold a crystal that has been forming for ten million years, 
                  you are holding time itself. You are connecting with the same molecular structures that witnessed the rise and fall 
                  of ancient seas, the shifting of continents, the dance of tectonic plates beneath your feet. This is not mere 
                  metaphor—this is physics, geology, and spirituality woven into a single thread of experience.
                </p>

                <div className="grid md:grid-cols-3 gap-6 my-8 text-center">
                  <div className="bg-gradient-to-br from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-xl p-6">
                    <Zap className="w-8 h-8 text-[#9D4EDD] mx-auto mb-3" />
                    <h3 className="font-serif text-lg text-[#1e1919] mb-2">Energy Alignment</h3>
                    <p className="text-sm">Restore balance to your body's electromagnetic field</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#FFB800]/10 to-[#00C853]/10 rounded-xl p-6">
                    <Heart className="w-8 h-8 text-[#FF6B9D] mx-auto mb-3" />
                    <h3 className="font-serif text-lg text-[#1e1919] mb-2">Emotional Healing</h3>
                    <p className="text-sm">Release old patterns and welcome inner peace</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#00B4D8]/10 to-[#9D4EDD]/10 rounded-xl p-6">
                    <Star className="w-8 h-8 text-[#FFB800] mx-auto mb-3" />
                    <h3 className="font-serif text-lg text-[#1e1919] mb-2">Spiritual Growth</h3>
                    <p className="text-sm">Deepen your connection to universal consciousness</p>
                  </div>
                </div>

                <p>
                  Your body is an intricate system of <strong className="text-[#9D4EDD]">energy centers</strong>—what ancient traditions call chakras. 
                  These spinning wheels of light govern everything from your sense of safety and belonging (root chakra) to your 
                  capacity for love and compassion (heart chakra) to your connection with divine intelligence (crown chakra). 
                  When these centers flow freely and remain in balance, you experience what sages have described for millennia: 
                  a state of wholeness, vitality, and deep inner peace. When they become blocked—through stress, trauma, 
                  environmental toxins, or simply the weight of modern living—you feel it as anxiety, fatigue, disconnection, 
                  and dis-ease.
                </p>

                <p>
                  Gemstones work by <em>resonance</em>. Each crystal vibrates at a specific frequency, and when that frequency 
                  matches the natural vibration of one of your energy centers, something remarkable happens: the crystal begins 
                  to "tune" your chakra back to its optimal state, much like a tuning fork brings a piano string into perfect pitch. 
                  This is why <strong className="text-[#FF6B9D]">Rose Quartz</strong> feels so soothing when placed over your heart—its gentle pink 
                  frequency resonates with the heart chakra's natural vibration of unconditional love. This is why 
                  <strong className="text-[#9D4EDD]"> Amethyst</strong> calms an overactive mind—its violet ray aligns with the third eye and crown, 
                  bringing mental chatter into peaceful stillness.
                </p>

                <p>
                  At Gemstone Minerals, we take this sacred science seriously. Every crystal in our collection has been 
                  <strong className="text-[#c9a96e]"> hand-selected</strong> not for appearance alone, but for its energetic integrity. We work with ethical 
                  suppliers who understand that how a stone is extracted from the Earth matters. We cleanse each piece through 
                  smoke purification, sound healing, and lunar bathing. And most importantly, we <strong className="text-[#c9a96e]">activate</strong> every 
                  gemstone before it reaches you—charging it with protective energy and, when you share your intentions with us, 
                  programming it specifically for your journey.
                </p>

                <p>
                  But we offer more than crystals. <strong className="text-[#1e1919]">Gemstone Minerals</strong> is a community of seekers, healers, 
                  and curious souls who understand that wellness is a practice, not a destination. Through our 
                  <strong className="text-[#9D4EDD]"> live webinars</strong> with industry specialists like Romona Nicole Schmidt, you'll learn 
                  professional techniques for space clearing, aura cleansing, and manifesting with crystals. Through our 
                  <strong className="text-[#9D4EDD]"> monthly membership</strong>, you'll receive ongoing guidance, exclusive content, and a physical 
                  crystal delivered to your door each month. This is the gemstone lifestyle: a commitment to living in harmony 
                  with Earth's sacred energies, one crystal at a time.
                </p>

                <p>
                  Whether you are drawn to the grounding stability of <strong>Black Onyx</strong>, the abundance-attracting shimmer of 
                  <strong> Green Aventurine</strong>, the angelic purity of <strong>Selenite</strong>, or the amplifying power of 
                  <strong> Clear Quartz</strong>, know this: the crystal that calls to you is the crystal you need. Trust your intuition. 
                  Honor your curiosity. And step into a life where balance, energy, and radiant wellness are not just goals—they 
                  are your natural state of being.
                </p>

                <p className="text-center mt-8 text-lg font-medium text-[#1e1919]">
                  Welcome to <span className="text-gradient-vibrant">Gemstone Minerals</span>. Your transformation begins here.
                </p>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 pt-8 border-t border-[#e8e4de] text-center">
              <p className="text-[#7A7A9A] mb-4">Ready to begin your crystal journey?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/collection"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-3 rounded-xl hover:shadow-xl transition-all"
                >
                  <Gem className="w-4 h-4" />
                  <span className="font-semibold">Browse Sacred Collection</span>
                </Link>
                <Link
                  to="/chakras"
                  className="inline-flex items-center gap-2 bg-[#1e1919] text-white px-8 py-3 rounded-xl hover:bg-[#2a2525] transition-all"
                >
                  <Zap className="w-4 h-4 text-[#FFB800]" />
                  <span className="font-semibold">Learn About Chakras</span>
                </Link>
                <Link
                  to="/webinar-registration"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFB800] to-[#FF9F43] text-[#1e1919] px-8 py-3 rounded-xl hover:shadow-lg transition-all"
                >
                  <Star className="w-4 h-4" />
                  <span className="font-semibold">Join a Webinar</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
